// Staff accounts: who can sign in, and which tabs each of them gets.
//
// Everything here is enforced on the server as well — this screen only makes
// it possible to say, it is not what makes it true.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../api';
import { useAuth } from '../auth';

const LABEL = {
  blanks: 'Blanks', prints: 'Prints', orders: 'Orders', returns: 'Returns',
  manifest: 'Manifest', cod: 'COD', dtf: 'DTF Sheet', alerts: 'Alerts', reviews: 'Reviews', followups: 'Follow-ups', products: 'Products', admin: 'Admin'
};

export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [pages, setPages] = useState([]);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, p] = await Promise.all([
        axios.get(`${API}/api/users`),
        axios.get(`${API}/api/users/pages`)
      ]);
      setUsers(u.data.users); setPages(p.data.pages); setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setUsers([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/users`, { email: email.trim(), role: 'staff', pages: picked });
      setEmail(''); setPicked([]);
      await load();
    } catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  const update = async (u, patch) => {
    try { await axios.patch(`${API}/api/users/${encodeURIComponent(u.email)}`, patch); await load(); }
    catch (e) { alert(e.response?.data?.message || e.message); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They lose access immediately.`)) return;
    try { await axios.delete(`${API}/api/users/${encodeURIComponent(u.email)}`); await load(); }
    catch (e) { alert(e.response?.data?.message || e.message); }
  };

  const togglePage = (u, page) => {
    const next = (u.pages || []).includes(page)
      ? u.pages.filter(p => p !== page)
      : [...(u.pages || []), page];
    update(u, { pages: next });
  };

  const chip = (on, disabled) => ({
    fontSize: 12, padding: '3px 9px', borderRadius: 20, cursor: disabled ? 'default' : 'pointer',
    border: '1px solid ' + (on ? '#6e1f2a' : '#e6dccc'),
    background: on ? '#57161f' : '#f8f3eb',
    color: on ? '#fff' : '#93827a', opacity: disabled ? 0.55 : 1
  });

  if (error) return <div className="card" style={{ color: '#b3261e' }}>{error}</div>;
  if (!users) return <p style={{ color: '#93827a' }}>Loading…</p>;

  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Give someone access</div>
        <p style={{ fontSize: 12, color: '#93827a', margin: '8px 0 14px' }}>
          They sign in with this Google address. Their name and photo arrive from Google
          the first time — nothing else to fill in. Tick the tabs they should see.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="name@gmail.com"
            style={{ width: 260 }} onKeyDown={e => { if (e.key === 'Enter') invite(); }} />
          <button className="btn-primary" onClick={invite} disabled={busy}
            style={{ padding: '8px 16px', fontSize: 13 }}>{busy ? 'Adding…' : 'Add staff'}</button>
        </div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 14 }}>
          {pages.filter(p => p !== 'admin').map(p => (
            <span key={p} onClick={() => setPicked(picked.includes(p) ? picked.filter(x => x !== p) : [...picked, p])}
              style={chip(picked.includes(p))}>{LABEL[p] || p}</span>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-title">Who can sign in ({users.length})</div>
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <table>
            <thead><tr><th>Person</th><th>Role</th><th>Can see</th><th>Last signed in</th><th></th></tr></thead>
            <tbody>
              {users.map(u => {
                const isMe = me && u.email === me.email;
                return (
                  <tr key={u.email} style={{ opacity: u.active ? 1 : 0.45 }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        {u.picture && <img src={u.picture} alt="" style={{ width: 26, height: 26, borderRadius: '50%' }} />}
                        <div>
                          <div style={{ fontWeight: 500 }}>{u.name || '—'} {isMe && <span style={{ color: '#93827a', fontSize: 11 }}>(you)</span>}</div>
                          <div style={{ fontSize: 12, color: '#93827a' }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <select value={u.role} disabled={isMe}
                        onChange={e => update(u, { role: e.target.value })}
                        style={{ width: 100, fontSize: 13 }}>
                        <option value="staff">Staff</option>
                        <option value="owner">Owner</option>
                      </select>
                    </td>
                    <td>
                      {u.role === 'owner'
                        ? <span style={{ fontSize: 12, color: '#2e7d50' }}>everything</span>
                        : (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxWidth: 380 }}>
                            {pages.filter(p => p !== 'admin').map(p => (
                              <span key={p} onClick={() => togglePage(u, p)} style={chip((u.pages || []).includes(p))}>
                                {LABEL[p] || p}
                              </span>
                            ))}
                          </div>
                        )}
                    </td>
                    <td style={{ fontSize: 12, color: '#7d6a63' }}>
                      {u.lastLogin ? new Date(u.lastLogin).toLocaleString() : 'never'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button onClick={() => update(u, { active: !u.active })} disabled={isMe}
                        style={{ fontSize: 12, padding: '4px 10px', marginRight: 6, borderRadius: 6, cursor: isMe ? 'default' : 'pointer',
                          background: '#ffffff', color: u.active ? '#7a6a12' : '#2e7d50', border: '1px solid #e6dccc', opacity: isMe ? 0.4 : 1 }}>
                        {u.active ? 'Suspend' : 'Restore'}
                      </button>
                      <button onClick={() => remove(u)} disabled={isMe}
                        style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: isMe ? 'default' : 'pointer',
                          background: '#ffffff', color: '#b3261e', border: '1px solid #e6dccc', opacity: isMe ? 0.4 : 1 }}>
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12, color: '#93827a', marginTop: 14 }}>
          Suspending keeps the record and takes access away on their next click. Removing deletes it.
          You cannot change your own access here — that is what stops the last way in being closed by accident.
        </p>
      </div>
    </div>
  );
}
